import { createFileRoute, Link } from '@tanstack/react-router';
import { ENV_CROSS_LINK_TO_CARBON_PLANNING } from '@siliconx/shared';
import { Card } from '@siliconx/shared/ui/Card';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import { ErrorState, LoadingShell } from '@siliconx/shared/ui/state';
import { KpiStrip } from '../../components/environmental/KpiStrip';
import { MonthlySeriesChart } from '../../components/environmental/MonthlySeriesChart';
import { AvoidedEventsTape } from '../../components/environmental/AvoidedEventsTape';
import { YoyTable } from '../../components/environmental/YoyTable';
import { BasisTag } from '../../components/environmental/BasisTag';
import { FyGoalTrackingCard } from '../../components/environmental/FyGoalTrackingCard';
import { Scope3CategoryMatrix } from '../../components/environmental/Scope3CategoryMatrix';
import { DataCoverageStrip } from '../../components/financials/sustainability/DataCoverageStrip';
import {
  useAvoidedEventsTape,
  useEnvironmentalOverviewQuery,
  useMonthlySeries,
  useYoyTable,
} from '../../hooks/useEnvironmentalOverview';

/**
 * SIL-472 — Environmental Overview tab, first demo slice. REAL: KPI strip
 * (total/scope2/scope3), monthly S2+S3 series, YoY table (SIL-487). SAMPLE:
 * AVOIDED/INTENSITY KPI tiles (inside KpiStrip), avoided-events tape —
 * every one basis-tagged, none of it built from the mock's banned
 * "gross inventory" bar or "% displaced" ratio.
 *
 * `FyGoalTrackingCard` (demo-feedback successor to the SIL-474/475
 * Fy30TargetCard mount) and `Scope3CategoryMatrix` (SIL-488 — replaces the
 * SIL-475 v0 ledger with the SIL-464 Variant B category x persona matrix)
 * fill the two shape-preserving placeholder slots SIL-472 shipped this
 * layout with.
 */
export const Route = createFileRoute('/environmental/')({
  component: OverviewTab,
});

function OverviewTab() {
  const overviewQ = useEnvironmentalOverviewQuery();
  const monthly = useMonthlySeries();
  const tape = useAvoidedEventsTape();
  const yoy = useYoyTable();

  return (
    <div className="space-y-4">
      {/* SIL-496: the return half of the SIL-486 cross-link pair. The
       * constant has existed since SIL-486 but nothing rendered it, so the
       * link only ever pointed one way — Carbon Planning -> here. Product and
       * teammates read the two surfaces as duplicated data; each one has to
       * say what the other is for. Wording is locked in lockstep with
       * CARBON_PLANNING_CROSS_LINK_TO_ENVIRONMENTAL — never reword one alone. */}
      <Link
        to="/financials/sustainability"
        className="block text-xs text-brand-700 hover:underline"
        data-testid="cross-link-carbon-planning"
      >
        {ENV_CROSS_LINK_TO_CARBON_PLANNING}
      </Link>

      <KpiStrip />

      {overviewQ.isLoading && <LoadingShell rows={4} />}
      {overviewQ.isError && (
        <ErrorState
          title="Could not load the Environmental overview"
          error={overviewQ.error}
          onRetry={() => void overviewQ.refetch()}
        />
      )}

      {overviewQ.data && (
        <div className="grid grid-cols-12 items-start gap-4">
          <Card padding="md" className="col-span-12">
            <div className="mb-3 flex items-center justify-between">
              <SectionLabel>Monthly emissions by scope</SectionLabel>
              <BasisTag basis={monthly.basis} />
            </div>
            <MonthlySeriesChart points={monthly.data ?? []} />
            {monthly.coverage && (
              <div className="mt-3">
                <DataCoverageStrip coverage={monthly.coverage} />
              </div>
            )}
          </Card>

          <div className="col-span-12 lg:col-span-8">
            <Scope3CategoryMatrix />
          </div>

          <div className="col-span-12 flex flex-col gap-4 lg:col-span-4">
            <FyGoalTrackingCard />
            <Card padding="md">
              <div className="mb-3 flex items-center justify-between">
                <SectionLabel>Year over year</SectionLabel>
                <BasisTag basis={yoy.basis} />
              </div>
              <YoyTable rows={yoy.data ?? []} />
            </Card>
          </div>

          <Card padding="md" className="col-span-12">
            <div className="mb-3 flex items-center justify-between">
              <SectionLabel>Recent avoided-emissions events</SectionLabel>
              <BasisTag basis={tape.basis} />
            </div>
            <AvoidedEventsTape events={tape.data} />
          </Card>
        </div>
      )}
    </div>
  );
}
